import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { InstagramAccount } from "../api/types";
import { timezoneLabel } from "../lib/timezones";
import { humanizeError, humanizeErrorKind } from "../lib/errorMessages";
import EditAccountModal from "./../components/EditAccountModal";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import Avatar from "../components/ui/Avatar";
import StatusDot from "../components/ui/StatusDot";
import {
  IconAccounts,
  IconLock,
  IconPencil,
  IconPlus,
  IconShield,
  IconTrash,
} from "../components/ui/Icons";

const STATUS_TONE: Record<
  InstagramAccount["status"],
  { dot: "success" | "warn" | "info" | "danger" | "neutral"; pill: string; pulse?: boolean }
> = {
  pending_login: { dot: "warn", pill: "bg-amber-500/15 text-amber-300 ring-amber-400/30", pulse: true },
  awaiting_2fa_code: { dot: "info", pill: "bg-sky-500/15 text-sky-300 ring-sky-400/30", pulse: true },
  active: { dot: "success", pill: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30", pulse: true },
  paused: { dot: "neutral", pill: "bg-slate-500/20 text-slate-300 ring-slate-500/30" },
  challenge_required: {
    dot: "warn",
    pill: "bg-orange-500/15 text-orange-300 ring-orange-400/30",
    pulse: true,
  },
  banned: { dot: "danger", pill: "bg-rose-500/15 text-rose-300 ring-rose-400/30" },
  error: { dot: "danger", pill: "bg-rose-500/15 text-rose-300 ring-rose-400/30" },
  verifying: { dot: "info", pill: "bg-sky-500/15 text-sky-300 ring-sky-400/30", pulse: true },
};

export default function InstagramAccounts() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);

  const accountsQuery = useQuery({
    queryKey: ["instagram-accounts"],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: InstagramAccount[] }>(
        "/api/instagram-accounts"
      );
      return data.accounts;
    },
    refetchInterval: (query) => {
      const accs = (query.state.data as InstagramAccount[] | undefined) || [];
      // Aggressive polling whenever ANY account is in a transient/problem state.
      // Without this, an account in "error" never refreshes — so when the user
      // clicks Verify and the worker transitions it to "awaiting_2fa_code"
      // server-side, the dashboard would keep showing "error" until the user
      // manually reloads, hiding the 2FA input the user needs to submit.
      return accs.some(
        (a) =>
          a.status === "pending_login" ||
          a.status === "awaiting_2fa_code" ||
          a.status === "verifying" ||
          a.status === "error" ||
          a.status === "challenge_required"
      )
        ? 2500
        : false;
    },
  });

  const verify = useMutation({
    mutationFn: async (id: string) => api.post(`/api/instagram-accounts/${id}/verify`),
    onSuccess: () => {
      toast.success(t("instagramAccounts.toast.verifyStarted"));
      qc.invalidateQueries({ queryKey: ["instagram-accounts"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`/api/instagram-accounts/${id}`),
    onSuccess: () => {
      toast.success(t("instagramAccounts.toast.removed"));
      qc.invalidateQueries({ queryKey: ["instagram-accounts"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const togglePause = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "active" | "paused" }) =>
      api.patch(`/api/instagram-accounts/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["instagram-accounts"] }),
    onError: (err) => toast.error(apiError(err)),
  });

  const accounts = accountsQuery.data || [];
  const summary = useMemo(() => {
    const active = accounts.filter((a) => a.status === "active").length;
    const paused = accounts.filter((a) => a.status === "paused").length;
    const attention = accounts.filter(
      (a) =>
        a.status === "challenge_required" ||
        a.status === "error" ||
        a.status === "awaiting_2fa_code"
    ).length;
    return { active, paused, attention, total: accounts.length };
  }, [accounts]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.instagramAccounts")}
        title={t("instagramAccounts.title")}
        subtitle={t("instagramAccounts.subtitle")}
        icon={<IconAccounts />}
        actions={
          <Link
            to="/instagram-accounts/new"
            className="btn-primary w-full justify-center sm:w-auto"
          >
            <IconPlus size={16} />
            {t("instagramAccounts.addAccount")}
          </Link>
        }
      />

      {accounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <SummaryChip
            tone="emerald"
            label={t("instagramAccounts.summary.active", { count: summary.active })}
          />
          <SummaryChip
            tone="slate"
            label={t("instagramAccounts.summary.paused", { count: summary.paused })}
          />
          {summary.attention > 0 && (
            <SummaryChip
              tone="amber"
              label={t("instagramAccounts.summary.needsAttention", {
                count: summary.attention,
              })}
            />
          )}
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500">
            <IconShield size={12} className="text-emerald-400" />
            AES-256-GCM
          </span>
        </div>
      )}

      {accountsQuery.isLoading ? (
        <div className="card">
          <p className="text-slate-400">{t("instagramAccounts.loading")}</p>
        </div>
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={<IconAccounts size={26} />}
          title={t("instagramAccounts.emptyState.title")}
          description={t("instagramAccounts.emptyState.subtitle")}
          action={
            <Link to="/instagram-accounts/new" className="btn-primary px-5 py-2.5">
              <IconPlus size={16} />
              {t("instagramAccounts.emptyState.cta")}
            </Link>
          }
        />
      ) : (
        <div className="space-y-3">
          {accounts.map((acc) => (
            <AccountRow
              key={acc.id}
              acc={acc}
              onVerify={() => verify.mutate(acc.id)}
              onTogglePause={() => {
                if (
                  acc.status === "error" ||
                  acc.status === "pending_login" ||
                  acc.status === "challenge_required"
                ) {
                  // "Reanudar y verificar" on errorish accounts is the same as
                  // clicking Verify — both run the lightweight verify flow,
                  // which transparently re-logs if the session expired.
                  verify.mutate(acc.id);
                } else {
                  togglePause.mutate({
                    id: acc.id,
                    status: acc.status === "active" ? "paused" : "active",
                  });
                }
              }}
              onEdit={() => setEditingId(acc.id)}
              onRemove={() => {
                if (
                  confirm(
                    t("instagramAccounts.confirmRemove", { username: acc.igUsername })
                  )
                )
                  remove.mutate(acc.id);
              }}
              verifyPending={verify.isPending}
            />
          ))}
        </div>
      )}

      {(() => {
        const editing = accounts.find((a) => a.id === editingId);
        if (!editing) return null;
        return (
          <EditAccountModal
            account={editing}
            open={true}
            onClose={() => setEditingId(null)}
          />
        );
      })()}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Summary chip
 * ------------------------------------------------------------------------ */

function SummaryChip({
  tone,
  label,
}: {
  tone: "emerald" | "slate" | "amber";
  label: string;
}): JSX.Element {
  const styles =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-300 ring-emerald-400/30"
      : tone === "amber"
      ? "bg-amber-500/15 text-amber-300 ring-amber-400/30"
      : "bg-slate-500/15 text-slate-300 ring-slate-500/30";
  return (
    <span className={clsx("rounded-full px-3 py-1 text-xs font-medium ring-1", styles)}>
      {label}
    </span>
  );
}

/* --------------------------------------------------------------------------
 *  Status reason details (preserved from previous design but cleaner)
 * ------------------------------------------------------------------------ */

function StatusReason({
  reason,
  status,
  kind,
  consecutiveFailures,
  lastAutoReloginAt,
}: {
  reason: string;
  status: InstagramAccount["status"];
  kind: string;
  consecutiveFailures: number;
  lastAutoReloginAt: string | null;
}) {
  const { t } = useTranslation();
  const friendly = humanizeErrorKind(kind, t) || humanizeError(reason, t);
  let guidance: string | null = null;
  if (status === "error") {
    guidance = t("instagramAccounts.guidance.retry");
  } else if (status === "challenge_required") {
    guidance = t("instagramAccounts.guidance.challenge");
  } else if (status === "pending_login" && kind === "bad_credentials") {
    guidance = t("instagramAccounts.guidance.badCredentials");
  } else if (status === "pending_login") {
    guidance = t("instagramAccounts.guidance.relogin");
  } else if (kind === "transient_network" || kind === "network_blip") {
    guidance = t("instagramAccounts.guidance.autoRetrying");
  }

  const showVerifyHint =
    status === "error" || status === "pending_login" || status === "challenge_required";

  const recentRecovery =
    lastAutoReloginAt &&
    Date.now() - new Date(lastAutoReloginAt).getTime() < 30 * 60_000;

  return (
    <div className="mt-2 space-y-1 rounded-lg border border-slate-800/80 bg-slate-950/40 p-3 text-xs">
      {recentRecovery && status === "active" && (
        <p className="text-emerald-400">{t("instagramAccounts.autoRecovered")}</p>
      )}
      <p className="text-slate-200">{friendly || reason}</p>
      {consecutiveFailures > 1 && (
        <p className="text-amber-400">
          {t("instagramAccounts.consecutiveFailures", { count: consecutiveFailures })}
        </p>
      )}
      {guidance && <p className="text-slate-400">{guidance}</p>}
      {showVerifyHint && (
        <p className="text-slate-500">{t("instagramAccounts.verifyHint")}</p>
      )}
      {friendly && (
        <details className="text-[11px] text-slate-500">
          <summary className="cursor-pointer hover:text-slate-300">
            {t("instagramAccounts.showTechnical")}
          </summary>
          <pre className="mt-1 max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded bg-slate-900/50 p-2 text-slate-400">
            {reason}
          </pre>
        </details>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Recent IP-rotation recovery banner
 *  Shown for ~30 min after a reCAPTCHA-triggered auto-rotation succeeded, so
 *  the artist sees that the platform recovered without their intervention.
 * ------------------------------------------------------------------------ */

function RotationRecoveryBanner({ acc }: { acc: InstagramAccount }) {
  const { t } = useTranslation();
  if (acc.status !== "active") return null;
  if (!acc.lastProxyRotationAt) return null;
  const ageMs = Date.now() - new Date(acc.lastProxyRotationAt).getTime();
  if (ageMs > 30 * 60_000) return null;
  return (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-100">
      <div className="font-medium">
        {t("instagramAccounts.proxyRotation.recovered")}
      </div>
      <p className="mt-0.5 text-emerald-200/80">
        {t("instagramAccounts.proxyRotation.recoveredBody")}
      </p>
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Account row
 * ------------------------------------------------------------------------ */

interface AccountRowProps {
  acc: InstagramAccount;
  onVerify: () => void;
  onTogglePause: () => void;
  onEdit: () => void;
  onRemove: () => void;
  verifyPending: boolean;
}

function AccountRow({
  acc,
  onVerify,
  onTogglePause,
  onEdit,
  onRemove,
  verifyPending,
}: AccountRowProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState("");

  const submit2fa = useMutation({
    mutationFn: async (theCode: string) =>
      api.post(`/api/instagram-accounts/${acc.id}/submit-2fa-code`, { code: theCode }),
    onSuccess: () => {
      toast.success(t("instagramAccounts.toast.codeSubmitted"));
      setCode("");
      qc.invalidateQueries({ queryKey: ["instagram-accounts"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const authMethodLabel =
    acc.authMethod === "totp"
      ? t("instagramAccounts.authMethod.totp")
      : acc.authMethod === "backup_codes"
      ? t("instagramAccounts.authMethod.backupCodes", { remaining: acc.backupCodesRemaining })
      : t("instagramAccounts.authMethod.interactive");

  const backupCodesLow =
    acc.authMethod === "backup_codes" && acc.backupCodesRemaining <= 2;

  const tone = STATUS_TONE[acc.status];

  return (
    <article className="card space-y-4">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Avatar name={acc.igUsername} size="lg" variant="instagram" />
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold tracking-tight text-white">
                @{acc.igUsername}
              </h3>
              <span className={clsx("pill ring-1", tone.pill)}>
                <StatusDot tone={tone.dot} pulse={tone.pulse} />
                {t(`instagramAccounts.status.${acc.status}`)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
              <span className="chip">
                <IconLock size={11} />
                {authMethodLabel}
              </span>
              {acc.effectiveProxySource === "user" && (
                <span className="chip" title={t("instagramAccounts.proxyChip.userTitle")}>
                  <IconShield size={11} />
                  {t("instagramAccounts.proxyChip.user")}
                </span>
              )}
              {acc.effectiveProxySource === "managed" && (
                <span
                  className="chip text-emerald-300"
                  title={t("instagramAccounts.proxyChip.managedTitle", {
                    country: (acc.effectiveProxyCountry || "").toUpperCase(),
                  })}
                >
                  <IconShield size={11} />
                  {t("instagramAccounts.proxyChip.managed", {
                    country: (acc.effectiveProxyCountry || "").toUpperCase(),
                  })}
                </span>
              )}
              {acc.effectiveProxySource === "none" && (
                <span
                  className="chip text-amber-300"
                  title={t("instagramAccounts.proxyChip.noneTitle")}
                >
                  {t("instagramAccounts.proxyChip.none")}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400">
              {acc.followerCount === 0 && acc.followingCount === 0
                ? t("instagramAccounts.statsUnknown", {
                    timezone: timezoneLabel(acc.timezone),
                  })
                : t("instagramAccounts.stats", {
                    followers: acc.followerCount.toLocaleString(),
                    following: acc.followingCount.toLocaleString(),
                    timezone: timezoneLabel(acc.timezone),
                  })}
            </p>
          </div>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:justify-end">
          <button
            className="btn-secondary btn-sm"
            onClick={onVerify}
            disabled={
              verifyPending ||
              acc.status === "banned" ||
              acc.status === "paused" ||
              acc.status === "verifying"
            }
            title={
              acc.status === "awaiting_2fa_code"
                ? t("instagramAccounts.verifyRestartTitle")
                : undefined
            }
          >
            {verifyPending || acc.status === "verifying"
              ? t("instagramAccounts.verifying")
              : acc.status === "awaiting_2fa_code"
              ? t("instagramAccounts.verifyRestart")
              : t("instagramAccounts.verify")}
          </button>
          {(acc.status === "active" || acc.status === "paused") && (
            <button className="btn-secondary btn-sm" onClick={onTogglePause}>
              {acc.status === "active"
                ? t("instagramAccounts.pause")
                : t("instagramAccounts.resume")}
            </button>
          )}
          <button
            className="btn-ghost btn-sm"
            onClick={onEdit}
            aria-label={t("instagramAccounts.edit")}
            title={t("instagramAccounts.edit")}
          >
            <IconPencil size={14} />
            <span className="hidden sm:inline">{t("instagramAccounts.edit")}</span>
          </button>
          <button
            className="btn-danger-ghost btn-sm"
            onClick={onRemove}
            aria-label={t("instagramAccounts.remove")}
            title={t("instagramAccounts.remove")}
          >
            <IconTrash size={14} />
            <span className="hidden sm:inline">{t("instagramAccounts.remove")}</span>
          </button>
        </div>
      </header>

      {acc.statusReason && (
        <StatusReason
          reason={acc.statusReason}
          status={acc.status}
          kind={acc.lastFailureKind}
          consecutiveFailures={acc.consecutiveFailures}
          lastAutoReloginAt={acc.lastAutoReloginAt}
        />
      )}

      <RotationRecoveryBanner acc={acc} />

      {backupCodesLow && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
          {t("instagramAccounts.backupCodesLow", { remaining: acc.backupCodesRemaining })}
        </div>
      )}

      {/* Hint when an "error"/"challenge_required" account previously failed
          login with auth_required. It almost always means IG asked for a 2FA
          code that we couldn't supply. We explicitly tell the user clicking
          Verify will reopen the login flow and the 2FA card will appear here
          for them to enter the code. Without this hint the user sees
          "Error · Llevamos N fallos seguidos" and doesn't know what to do. */}
      {(acc.status === "error" || acc.status === "challenge_required") &&
        acc.lastFailureKind === "auth_required" && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
            <p className="font-semibold">{t("instagramAccounts.twoFa.errorHintTitle")}</p>
            <p className="mt-1 text-[11px] text-amber-200/80">
              {t("instagramAccounts.twoFa.errorHintBody")}
            </p>
          </div>
        )}

      {acc.status === "awaiting_2fa_code" && (() => {
        // Instagram 2FA codes expire after ~10 minutes. If the account has
        // been waiting longer than that, the code in the user's email is
        // almost certainly dead — and submitting it would just fail and
        // bump the account into challenge_required. Surface a clear "ask
        // for a new code" path instead of silently letting them paste an
        // expired one.
        const STALE_2FA_MS = 10 * 60 * 1000;
        const waitingSince = acc.awaiting2faSince
          ? new Date(acc.awaiting2faSince).getTime()
          : 0;
        const isStale = waitingSince > 0 && Date.now() - waitingSince > STALE_2FA_MS;
        return (
          <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/20 text-sky-300">
                <IconLock size={16} />
              </span>
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold text-sky-100">
                  {t("instagramAccounts.twoFa.heading")}
                </p>
                <p className="text-xs text-slate-400">{t("instagramAccounts.twoFa.help")}</p>
              </div>
            </div>
            {isStale ? (
              <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
                <p className="font-semibold">
                  {t("instagramAccounts.twoFa.expiredTitle")}
                </p>
                <p className="mt-1 text-[11px] text-amber-200/80">
                  {t("instagramAccounts.twoFa.expiredBody")}
                </p>
                <button
                  type="button"
                  className="btn-primary btn-sm mt-2"
                  onClick={onVerify}
                  disabled={verifyPending}
                >
                  {verifyPending
                    ? t("instagramAccounts.verifying")
                    : t("instagramAccounts.twoFa.requestNewCode")}
                </button>
              </div>
            ) : null}
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                inputMode="numeric"
                maxLength={20}
                className="input font-mono tracking-widest"
                placeholder={t("instagramAccounts.twoFa.placeholder")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <button
                className="btn-primary"
                disabled={code.trim().length < 4 || submit2fa.isPending}
                onClick={() => submit2fa.mutate(code.trim())}
              >
                {submit2fa.isPending
                  ? t("instagramAccounts.twoFa.submitting")
                  : t("instagramAccounts.twoFa.submit")}
              </button>
            </div>
          </div>
        );
      })()}
    </article>
  );
}
